/* eslint-disable jsx-a11y/no-autofocus */
import type { CustomCellEditorProps } from 'ag-grid-react';
import type { FC, KeyboardEvent } from 'react';
import { useCallback, useEffect, useRef } from 'react';

import { useGridCellEditor } from 'ag-grid-react';

import { NumberInput } from '../NumberInput';

export interface NumericCellFieldProps extends CustomCellEditorProps {
  onValueChange: (value: unknown) => void;
  defaultValue?: number;
  decimalScale?: number;
  fixedDecimalScale?: boolean;
}

const KEY_BACKSPACE = 'Backspace';
const KEY_F2 = 'F2';
const KEY_ENTER = 'Enter';
const KEY_TAB = 'Tab';

const isLeftOrRight = (event: KeyboardEvent) => ['ArrowLeft', 'ArrowRight'].includes(event.key);

const isBackspace = (event: KeyboardEvent) => event.key === KEY_BACKSPACE;

const finishedEditingPressed = (event: KeyboardEvent) => {
  const key = event.key;

  return key === KEY_ENTER || key === KEY_TAB;
};

export const NumericCellField: FC<NumericCellFieldProps> = (props) => {
  const { decimalScale, fixedDecimalScale, value, onValueChange, eventKey, stopEditing } = props;

  const refInput = useRef<HTMLInputElement>(null);

  const updateValue = useCallback(
    (val?: number, textValue?: string) => {
      // "-" is an intermediate value while typing a negative number.
      if (textValue === '-') {
        return;
      }

      // An empty input represents null.
      // Do not use `!val`, because 0 is a valid number.
      onValueChange(val ?? null);
    },
    [onValueChange],
  );

  useEffect(() => {
    let startValue: unknown;
    let highlightAllOnFocus = true;

    if (eventKey === KEY_BACKSPACE) {
      // Clear the cell when editing starts with Backspace.
      startValue = '';
    } else if (eventKey?.length === 1) {
      // Start with the pressed character.
      startValue = eventKey;
      highlightAllOnFocus = false;
    } else {
      // Start with the current value.
      startValue = value;

      if (eventKey === KEY_F2) {
        highlightAllOnFocus = false;
      }
    }

    startValue ??= '';

    updateValue(startValue as number | undefined);

    const input = refInput.current;

    if (!input) {
      return;
    }

    input.focus();

    if (highlightAllOnFocus) {
      input.select();
    } else {
      // Place the caret at the end when editing with F2
      // or when starting with a printable character.
      const length = input.value.length;

      if (length > 0) {
        input.setSelectionRange(length, length);
      }
    }

    // The editor should initialize only once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (isLeftOrRight(event) || isBackspace(event)) {
      event.stopPropagation();
      return;
    }

    if (finishedEditingPressed(event)) {
      stopEditing();
    }
  };

  useGridCellEditor({
    // null is a valid value, so the edit must not be cancelled.
    isCancelAfterEnd: () => false,
  });

  return (
    <NumberInput
      autoFocus
      decimalScale={decimalScale}
      fixedDecimalScale={fixedDecimalScale}
      getInputRef={refInput}
      onChange={updateValue}
      onKeyDown={onKeyDown}
      value={value ?? ''}
    />
  );
};
